import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../context/ToastContext'
import { useWebSocket } from '../context/WebSocketContext'
import { gamesApi } from '../api/games'

export default function Lobby() {
    const [games, setGames] = React.useState([])
    const [invitations, setInvitations] = React.useState([])
    const [loading, setLoading] = React.useState(true)
    const { user } = useAuth()
    const { addToast } = useToast()
    const { lastMessage } = useWebSocket()
    const navigate = useNavigate()

    async function fetchData() {
        try {
            const data = await gamesApi.listGames()
            setGames(data.games || [])
            setInvitations(data.pending_invitations || [])
        } catch (err) {
            addToast(err.message, 'error')
        } finally {
            setLoading(false)
        }
    }

    React.useEffect(() => { fetchData() }, []) // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
        if (!lastMessage) return
        const { event } = lastMessage
        if (event === 'game_invitation' || event === 'player_joined' || event === 'game_started') {
            fetchData()
        }
    }, [lastMessage]) // eslint-disable-line react-hooks/exhaustive-deps

    async function handleRespond(invitationId, accept) {
        try {
            await gamesApi.respondToInvitation(invitationId, accept)
            addToast(accept ? 'Invitation accepted!' : 'Invitation declined', accept ? 'success' : 'info')
            fetchData()
        } catch (err) {
            addToast(err.message, 'error')
        }
    }

    async function handleNewGame() {
        try {
            const game = await gamesApi.createGame()
            navigate(`/game/${game.id}/setup`)
        } catch (err) {
            addToast(err.message, 'error')
        }
    }

    function handlePlay(game) {
        navigate(game.status === 'pending' ? `/game/${game.id}/setup` : `/game/${game.id}`)
    }

    const statusBadge = {
        pending:   'bg-yellow-100 text-yellow-800',
        active:    'bg-green-100 text-green-800',
        complete:  'bg-gray-200 text-gray-600',
        abandoned: 'bg-gray-200 text-gray-600',
    }

    if (loading) {
        return (
            <main className="flex min-h-screen w-full items-center justify-center bg-bankroll-navy">
                <p className="text-gray-300">Loading…</p>
            </main>
        )
    }

    return (
        <main className="page-main">
            <div className="flex justify-end">
                <button className="px-4 py-2 border-0 rounded-md bg-bankroll-primary text-white font-karla text-sm font-bold cursor-pointer hover:opacity-85" onClick={handleNewGame}>New Game</button>
            </div>

            {invitations.length > 0 && (
                <section>
                    <h3 className="text-base font-bold text-gray-300 mb-3">Invitations</h3>
                    <ul className="list-none m-0 p-0 flex flex-col gap-2.5">
                        {invitations.map(inv => (
                            <li key={inv.id} className="bg-white rounded-[10px] px-4 py-3.5 flex items-center justify-between gap-3 shadow-sm border-l-4 border-bankroll-primary">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 flex-1">
                                    <span className="text-xs text-gray-400 font-bold uppercase tracking-wide">Invited by</span>
                                    <span className="text-[0.95rem] font-bold text-bankroll-text">{inv.invited_by_username}</span>
                                </div>
                                <div className="flex gap-2">
                                    <button className="px-4 py-2 border-0 rounded-md bg-bankroll-success text-white font-karla text-sm font-bold cursor-pointer hover:opacity-85" onClick={() => handleRespond(inv.id, true)}>Accept</button>
                                    <button className="px-4 py-2 border-0 rounded-md bg-gray-200 text-gray-600 font-karla text-sm font-bold cursor-pointer hover:opacity-85" onClick={() => handleRespond(inv.id, false)}>Decline</button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </section>
            )}

            <section>
                <h3 className="text-base font-bold text-gray-300 mb-3">My Games</h3>
                {games.filter(g => g.status === 'pending' || g.status === 'active').length === 0 ? (
                    <p className="text-gray-400 text-[0.95rem] m-0">No active games. Start one from the menu.</p>
                ) : (
                    <ul className="list-none m-0 p-0 flex flex-col gap-2.5">
                        {games.filter(g => g.status === 'pending' || g.status === 'active').map(game => (
                            <li key={game.id} className="bg-white rounded-[10px] px-4 py-3.5 flex items-center justify-between gap-3 shadow-sm">
                                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 flex-1">
                                    <span className="text-xs text-gray-400 font-bold uppercase tracking-wide">Players</span>
                                    <span className="text-[0.95rem] font-bold text-bankroll-text">{game.players?.length ?? 0}</span>
                                    <span className="text-xs text-gray-400 font-bold uppercase tracking-wide">Status</span>
                                    <span className={`text-xs font-bold rounded-full px-2.5 py-0.5 ${statusBadge[game.status] ?? 'bg-gray-200 text-gray-600'}`}>
                                        {game.status === 'pending' ? 'Waiting' : game.status === 'active' ? 'In progress' : 'Finished'}
                                    </span>
                                </div>
                                {(game.status === 'pending' || game.status === 'active') && (
                                    <button className="px-4 py-2 border-0 rounded-md bg-bankroll-primary text-white font-karla text-sm font-bold cursor-pointer hover:opacity-85" onClick={() => handlePlay(game)}>
                                        {game.status === 'pending' ? 'Setup' : 'Play'}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </main>
    )
}
